/**
 * Working out what a doctor is available for on a given date, and turning an
 * edit to that into the request which records it.
 *
 * Every layer records time the doctor is free, never time they are busy.
 * Availability for a date comes from exactly one of three — an override the
 * doctor painted, the free time left in the default week once their calendar's
 * busy periods are taken out of it, or the repeating default week itself — so a
 * whole day resolves to a single source rather than being blended together.
 * Everything here is pure, so the resolution and the request it produces can be
 * tested without a grid.
 */
import type {
  DateRange,
  Dayjs,
  IsoDate,
  TimeRange,
  TimeRangeOnWeekday,
} from "@thalamos/common";
import {
  DateRangeSchema,
  datesInRange,
  expandWeekdaysToDates,
  ISO_DATE_FORMAT,
  IsoDateSchema,
  mergeAdjacentTimeRanges,
  TimeOfDaySchema,
} from "@thalamos/common";
import type {
  Section12AvailabilityOverrideDate,
  SetSection12AvailabilityOverrideDatesRequestUnbranded,
} from "@thalamos/aspire-common";

const DAYS_PER_WEEK = 7;

/** Which layer decided a date: the doctor's override, an imported calendar, or the default week. */
export type ResolvedDaySource = "manual" | "synced" | "default";

/** One date of the calendar: what it offers, and which layer decided it. */
export interface ResolvedDay {
  date: IsoDate;
  source: ResolvedDaySource;
  blocks: TimeRange[];
}

/** A date the doctor has overridden by hand, with everything it still offers. */
export interface ManualDay {
  date: IsoDate;
  blocks: TimeRange[];
}

/** The seven dates of the week beginning `weekStart`, both ends included. */
export function weekRangeFrom(weekStart: Dayjs): DateRange {
  return DateRangeSchema.parse({
    from: weekStart.format(ISO_DATE_FORMAT),
    to: weekStart.add(DAYS_PER_WEEK - 1, "day").format(ISO_DATE_FORMAT),
  });
}

/**
 * Brands wire times and merges what touches, so a day reads as the windows it
 * offers rather than as the pieces they arrived in.
 */
function toTimeRanges(
  blocks: readonly { from: string; until: string }[],
): TimeRange[] {
  return mergeAdjacentTimeRanges(
    blocks.map((block) => ({
      from: TimeOfDaySchema.parse(block.from),
      until: TimeOfDaySchema.parse(block.until),
    })),
  );
}

function toWireBlocks(
  blocks: readonly TimeRange[],
): { from: string; until: string }[] {
  return blocks.map((block) => ({ from: block.from, until: block.until }));
}

/**
 * What each date of the week beginning `weekStart` offers.
 *
 * An override the doctor painted wins even when it offers nothing at all: an
 * empty day is how being away is recorded, and is not the same as having no
 * override.
 */
export function resolveWeek(
  weekStart: Dayjs,
  defaultWeek: readonly TimeRangeOnWeekday[],
  overrides: readonly Section12AvailabilityOverrideDate[],
): ResolvedDay[] {
  const range = weekRangeFrom(weekStart);
  const fromDefaults = expandWeekdaysToDates(defaultWeek, range);

  return datesInRange(range).map((date) => {
    const manual = overrides.find(
      (override) => override.date === date && override.source === "manual",
    );
    if (manual !== undefined) {
      return { date, source: "manual", blocks: toTimeRanges(manual.blocks) };
    }

    const synced = overrides.find(
      (override) =>
        override.date === date && override.source === "calendar-sync",
    );
    if (synced !== undefined) {
      return { date, source: "synced", blocks: toTimeRanges(synced.blocks) };
    }

    return {
      date,
      source: "default",
      blocks: toTimeRanges(fromDefaults.filter((entry) => entry.date === date)),
    };
  });
}

/** The overrides the doctor painted that already sit inside `range`. */
export function manualDaysIn(
  overrides: readonly Section12AvailabilityOverrideDate[],
  range: DateRange,
): ManualDay[] {
  return overrides
    .filter(
      (override) =>
        override.source === "manual" &&
        override.date >= range.from &&
        override.date <= range.to,
    )
    .map((override) => ({
      date: IsoDateSchema.parse(override.date),
      blocks: toTimeRanges(override.blocks),
    }))
    .sort((left, right) => left.date.localeCompare(right.date));
}

/**
 * The request that records `edited`.
 *
 * The range covers only the dates edited, so a save from one device disturbs as
 * little as possible of what another may have written. Within that range the
 * server replaces everything, clearing any date the request does not list —
 * which is why every override already inside the span is re-sent alongside the
 * edits, away days included. An away day's block list is empty, and dropping it
 * for being empty would delete the very thing it records.
 *
 * `unconfirmed` holds overrides already sent but not yet read back, so they are
 * not in `overrides` yet. They are as real as anything the server holds and
 * take precedence over it: a save that left them out would clear them.
 */
export function buildOverrideRequest(
  edited: ReadonlyMap<IsoDate, readonly TimeRange[]>,
  overrides: readonly Section12AvailabilityOverrideDate[],
  unconfirmed: ReadonlyMap<IsoDate, readonly TimeRange[]> = new Map(),
): SetSection12AvailabilityOverrideDatesRequestUnbranded {
  const editedDates = [...edited.keys()].sort();
  const from = editedDates[0];
  const to = editedDates.at(-1);
  if (from === undefined || to === undefined) {
    throw new Error("An override request needs at least one edited date");
  }
  const dateRange = DateRangeSchema.parse({ from, to });

  const existing = new Map<IsoDate, readonly TimeRange[]>();
  for (const day of manualDaysIn(overrides, dateRange)) {
    existing.set(day.date, day.blocks);
  }
  for (const [date, blocks] of unconfirmed) {
    if (date >= dateRange.from && date <= dateRange.to) {
      existing.set(date, blocks);
    }
  }

  const days = [...edited].map(([date, blocks]) => ({
    date,
    blocks: toWireBlocks(blocks),
  }));
  for (const [date, blocks] of existing) {
    if (!edited.has(date)) {
      days.push({ date, blocks: toWireBlocks(blocks) });
    }
  }

  return {
    dateRange,
    days: days.sort((left, right) => left.date.localeCompare(right.date)),
  };
}
